<template>
  <div class="min-h-screen bg-gray-100 py-4">
    <!-- Loading Component -->
    <loading :active.sync="loading" :is-full-page="fullPage"></loading>

    <!-- Main Container -->
    <div class="max-w-7xl mx-auto px-4">
      <div class="bg-white shadow rounded-lg">
        
        <!-- Header -->
        <div id="noShow" class="bg-white border-b px-6 py-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <button
                class="bg-red-500 hover:bg-red-600 text-white rounded-full w-10 h-10 flex items-center justify-center mr-4"
                @click="RouterBack()"
              >
                <i class="material-icons">chevron_left</i>
              </button>
              <h1 class="text-xl font-bold text-green-700">{{ $t('payement_details') }}</h1>
            </div>
            
            <button
              @click="openPopup"
              class="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded flex items-center"
            >
              <i class="material-icons mr-2">print</i>
              <span>Print</span>
            </button>
          </div>
        </div>

        <!-- Invoice Content -->
        <div id="detailP" class="p-6">
          
          <!-- Invoice Header -->
          <div class="flex justify-between items-start mb-8 pb-4 border-b">
            <div>
              <h2 class="text-3xl font-bold text-gray-800 mb-2">
                Invoice <span class="text-red-600">#{{ formData.id }}</span>
              </h2>
            </div>
            <div class="text-right logosection">
              <img
                src="https://storeno.b-cdn.net/colismartgo/logo.jpeg"
                alt="Colis'MART"
                class="h-16 w-auto opacity-80 mb-2 logo"
              />
            </div>
          </div>

          <!-- Invoice Details -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            
            <div class="bg-gray-50 border rounded p-4">
              <h3 class="text-sm font-semibold text-gray-600 mb-2">{{ $t('seller') }}</h3>
              <p class="text-gray-800 font-medium">
                {{ formData.seller ? (formData.seller.fullName || formData.seller.company) : 'N/A' }}
              </p>
            </div>

            <div class="bg-gray-50 border rounded p-4">
              <h3 class="text-sm font-semibold text-gray-600 mb-2">{{ $t('warehouses') }}</h3>
              <div v-if="formData.warehouses && formData.warehouses.length > 0">
                <span v-for="(war, j) in formData.country" :key="j" class="text-gray-800 font-medium">
                  {{ $f.getCountryNameByCode(options, war) }}
                  <span v-if="j != formData.country.length - 1">, </span>
                </span>
              </div>
              <span v-else class="text-gray-800 font-medium">
                {{ formData.country ? $f.getCountryNameByCode(options, formData.country[0]) : 'N/A' }}
              </span>
            </div>

            <div class="bg-gray-50 border rounded p-4">
              <h3 class="text-sm font-semibold text-gray-600 mb-2">{{ $t('payement_method') }}</h3>
              <p class="text-gray-800 font-medium">{{ formData.method }}</p>
            </div>

            <div class="bg-gray-50 border rounded p-4">
              <h3 class="text-sm font-semibold text-gray-600 mb-2">{{ $t('date_payement') }}</h3>
              <p class="text-gray-800 font-medium">{{ $moment(formData.date).format('yyyy-MM-DD') }}</p>
            </div>
          </div>

          <!-- Period and Order Count -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" v-if="formData.intervalDate && formData.intervalDate.fromDate && formData.intervalDate.toDate">
            <div class="bg-blue-50 border border-blue-200 rounded p-4">
              <h3 class="text-sm font-semibold text-blue-600 mb-2">{{ $t('Periode') }}</h3>
              <p class="text-blue-800 font-medium">
                {{ $moment(formData.intervalDate.fromDate).format('yyyy-MM-DD') }} - 
                {{ $moment(formData.intervalDate.toDate).format('yyyy-MM-DD') }}
              </p>
            </div>
            <div class="bg-green-50 border border-green-200 rounded p-4">
              <h3 class="text-sm font-semibold text-green-600 mb-2">Number of Orders</h3>
              <p class="text-green-800 font-medium text-xl">{{ formData.count }}</p>
            </div>
          </div>

          <!-- Orders Section -->
          <div class="mb-8">
            <h3 class="text-lg font-bold text-gray-800 mb-4 pb-2 border-b">Orders</h3>
            
            <div v-for="(group, index) in dataGroupedByCountry" :key="index" class="mb-6 border rounded overflow-hidden">
              
              <!-- Country Header -->
              <div class="bg-gray-700 text-white px-4 py-2">
                <h4 class="font-semibold">
                  {{ $f.getCountryNameByCode(options, group.country) || $f.getCountryNameByCode(options, formData.country[0]) }}
                  <small class="ml-2">({{ group.count }} orders)</small>
                </h4>
              </div>
              
              <!-- Orders Table -->
              <div class="overflow-x-auto">
                <table class="w-full">
                  <thead class="bg-green-200">
                    <tr>
                      <th class="px-3 py-2 text-left text-xs font-semibold text-green-700 uppercase">#</th>
                      <th class="px-3 py-2 text-left text-xs font-semibold text-green-700 uppercase">{{ $t('order_id') }}</th>
                      <th class="px-3 py-2 text-left text-xs font-semibold text-green-700 uppercase">Product Name</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">{{ $t('total_price') }}</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">{{ $t('confirmation_fees') }}</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">{{ $t('Upsell fees') }}</th>
                      <th v-if="formData.details[0] && formData.details[0].fulfillement_fees_vat" class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">{{ $t('fulfillement_fees') }}</th>
                      <th v-if="formData.country == 'MA'" class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">{{ $t('city') }}</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">{{ $t('shipping_fees') }}</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">{{ $t('total') }}</th>
                    </tr>
                  </thead>
                  
                  <tbody>
                    <tr v-for="(item, index) in group.orders" :key="item._id" 
                        :class="[
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                          (item.affiliate && item.affiliate.type) || (item.POD && item.POD.name) ? 'bg-teal-100' : ''
                        ]">
                      <td class="px-3 py-2 text-sm font-semibold">{{ index + 1 }}</td>
                      <td class="px-3 py-2 text-sm font-semibold">{{ item.order.id }}</td>
                      <td class="px-3 py-2 text-sm text-gray-700">
                        {{ item.order.details && item.order.details[0] && item.order.details[0].name ? ProductNameSplit(item.order.details) : item.products || '' }}
                      </td>
                      <td class="px-3 py-2 text-sm text-center">
                        {{ (item.shipping_refund_vat != 0 || item.shipping_cancel_vat != 0) ? 0 : item.order.totalPrice }}
                        <small v-if="item.affiliate && item.affiliate.type" class="text-blue-600 block">(Affiliate)</small>
                        <small v-if="item.POD && item.POD.name" class="text-purple-600 block">(POD)</small>
                      </td>
                      <td class="px-3 py-2 text-sm text-center">{{ item.confirmation_vat }}</td>
                      <td class="px-3 py-2 text-sm text-center">{{ item.upsell_vat || '' }}</td>
                      <td v-if="formData.details[0] && formData.details[0].fulfillement_fees_vat != 0" class="px-3 py-2 text-sm text-center">{{ item.fulfillement_fees_vat }}</td>
                      <td v-if="formData.country == 'MA'" class="px-3 py-2 text-sm text-center">{{ item.order.customer.city }}</td>
                      <td class="px-3 py-2 text-sm text-center">{{ item.shipping_vat }}</td>
                      <td class="px-3 py-2 text-sm text-center font-bold">
                        {{ item.amount }}
                        <small v-if="item.affiliate && item.affiliate.type || (item.POD && item.POD.name)" class="text-green-600 block">(Commission)</small>
                      </td>
                    </tr>
                  </tbody>
                  
                  <!-- Group Summary -->
                  <tfoot v-if="dataGroupedByCountry.length > 1" class="bg-gray-100 border-t">
                    <tr>
                      <td colspan="6" class="px-3 py-2 text-right text-sm font-semibold">Sales:</td>
                      <td colspan="4" class="px-3 py-2 text-center text-sm font-semibold">
                        {{ group.sales.toFixed(2) }} <sup>{{ group.currency }}</sup>
                      </td>
                    </tr>
                    <tr>
                      <td colspan="6" class="px-3 py-2 text-right text-sm font-semibold">Fees:</td>
                      <td colspan="4" class="px-3 py-2 text-center text-sm font-semibold">
                        {{ group.fees.toFixed(2) }} <sup>{{ group.currency }}</sup>
                      </td>
                    </tr>
                    <tr>
                      <td colspan="6" class="px-3 py-2 text-right text-sm font-semibold">Amount:</td>
                      <td colspan="4" class="px-3 py-2 text-center text-sm font-semibold">
                        {{ group.amount.toFixed(2) }} <sup>{{ group.currency }}</sup>
                      </td>
                    </tr>
                    <tr class="bg-red-100">
                      <td colspan="6" class="px-3 py-2 text-right text-sm font-bold text-red-700">Total MAD:</td>
                      <td colspan="4" class="px-3 py-2 text-center text-sm font-bold text-red-700">
                        {{ (group.amount / formData.infoCurrency.find(o => o.currencyName === group.currency).currency).toFixed(2) }} <sup>MAD</sup>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          <!-- Credits Section -->
          <div v-if="dataGroupedRefundsByCountry.length > 0 || dataGroupedExpeditionsByCountry.length > 0 || dataGroupedChargesByCountry.length > 0" class="mb-8">
            <h3 class="text-lg font-bold text-gray-800 mb-4 pb-2 border-b">Credits</h3>
            
            <!-- Refunds Table -->
            <div v-if="dataGroupedRefundsByCountry.length > 0" class="mb-6 border border-red-200 rounded overflow-hidden">
              <div class="bg-red-200 px-4 py-2 border-b">
                <h4 class="font-semibold text-red-800">Remboursements ({{ formData.refunds.length }})</h4>
              </div>
              
              <div class="overflow-x-auto">
                <table class="w-full">
                  <thead class="bg-green-200">
                    <tr>
                      <th class="px-3 py-2 text-left text-xs font-semibold text-green-700 uppercase">#</th>
                      <th class="px-3 py-2 text-left text-xs font-semibold text-green-700 uppercase">{{ $t('warehouse') }}</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">ORDER_ID</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">{{ $t('customer') }}</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">{{ $t('date') }}</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">{{ $t('traking_number') }}</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">{{ $t('details') }}</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">{{ $t('total_price') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in formData.refunds" :key="item._id" 
                        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                      <td class="px-3 py-2 text-sm font-semibold">{{ index + 1 }}</td>
                      <td class="px-3 py-2 text-sm">{{ $f.getCountryNameByCode(options, item.country) }}</td>
                      <td class="px-3 py-2 text-sm font-semibold">{{ item.shipping.orderObjet.id }}</td>
                      <td class="px-3 py-2 text-sm">
                        <div v-if="item.shipping.orderObjet" class="text-blue-900">{{ item.shipping.orderObjet.customer.fullName }}</div>
                        <div v-if="item.shipping.orderObjet.customer" class="text-gray-700 text-xs">{{ item.shipping.orderObjet.customer.phone }}</div>
                      </td>
                      <td class="px-3 py-2 text-sm">{{ $moment(item.shipping.date).format('yyyy-MM-DD') }}</td>
                      <td class="px-3 py-2 text-sm font-semibold">{{ item.shipping.trackingNumber }}</td>
                      <td class="px-3 py-2 text-sm">{{ item.shipping.orderObjet.details.length }} Products</td>
                      <td class="px-3 py-2 text-sm font-semibold">{{ item.fees_amount }} <sup>{{ item.currency }}</sup></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Expeditions Table -->
            <div v-if="dataGroupedExpeditionsByCountry.length > 0" class="mb-6 border border-blue-200 rounded overflow-hidden">
              <div class="bg-blue-200 px-4 py-2 border-b">
                <h4 class="font-semibold text-blue-800">Expeditions ({{ formData.expeditions.length }})</h4>
              </div>
              
              <div class="overflow-x-auto">
                <table class="w-full">
                  <thead class="bg-green-200">
                    <tr>
                      <th class="px-3 py-2 text-left text-xs font-semibold text-green-700 uppercase">#</th>
                      <th class="px-3 py-2 text-left text-xs font-semibold text-green-700 uppercase">{{ $t('warehouse') }}</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">ID</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">Weight (KG)</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">Packages</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">Reception Date</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">Products</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">From</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">Expedition Fees</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in formData.expeditions" :key="item._id" 
                        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                      <td class="px-3 py-2 text-sm font-semibold">{{ index + 1 }}</td>
                      <td class="px-3 py-2 text-sm">{{ $f.getCountryNameByCode(options, item.country) }}</td>
                      <td class="px-3 py-2 text-sm font-semibold">{{ item.id.id }}</td>
                      <td class="px-3 py-2 text-sm">{{ item.id.type && item.id.type === 'transfer' ? '' : (item.id.weight || 0) }}</td>
                      <td class="px-3 py-2 text-sm">{{ item.id.type && item.id.type === 'transfer' ? (item.id.numberOfPackages || 0) : '' }}</td>
                      <td class="px-3 py-2 text-sm">{{ $moment(item.id.arrivalDate).format('yyyy-MM-DD') }}</td>
                      <td class="px-3 py-2 text-sm" v-html="ProductGroupNmae(item.id, item.currency)"></td>
                      <td class="px-3 py-2 text-sm">{{ $f.getCountryNameByCode(optionsCountries, item.id.country) }}</td>
                      <td class="px-3 py-2 text-sm font-semibold">{{ item.fees_amount }} <sup>{{ item.currency }}</sup></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Charges Table -->
            <div v-if="dataGroupedChargesByCountry.length > 0" class="mb-6 border border-yellow-200 rounded overflow-hidden">
              <div class="bg-yellow-200 px-4 py-2 border-b">
                <h4 class="font-semibold text-yellow-800">Charges ({{ formData.charges.length }})</h4>
              </div>
              
              <div class="overflow-x-auto">
                <table class="w-full">
                  <thead class="bg-green-200">
                    <tr>
                      <th class="px-3 py-2 text-left text-xs font-semibold text-green-700 uppercase">#</th>
                      <th class="px-3 py-2 text-left text-xs font-semibold text-green-700 uppercase">{{ $t('warehouse') }}</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">ID</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">Comment</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">Date</th>
                      <th class="px-3 py-2 text-center text-xs font-semibold text-green-700 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in formData.charges" :key="item._id" 
                        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                      <td class="px-3 py-2 text-sm font-semibold">{{ index + 1 }}</td>
                      <td class="px-3 py-2 text-sm">{{ $f.getCountryNameByCode(options, item.country) }}</td>
                      <td class="px-3 py-2 text-sm font-semibold">{{ item.id.id }}</td>
                      <td class="px-3 py-2 text-sm">{{ item.id.comment }}</td>
                      <td class="px-3 py-2 text-sm">{{ $moment(item.id.date).format('yyyy-MM-DD') }}</td>
                      <td class="px-3 py-2 text-sm font-semibold">{{ item.fees_amount }} <sup>{{ item.currency }}</sup></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Total Summary -->
          <div class="border-t pt-4">
            <div class="w-full md:w-4/5 ml-auto">
              
              <div class="mb-4">
                <div class="flex justify-between mb-2">
                  <span class="text-gray-800">Total Sales:</span>
                  <div v-if="formData.amountCurrency && formData.infoCurrency && formData.infoCurrency.length > 0 && formData.amountCurrency.length > 0" class="font-medium text-gray-800">
                    <span v-for="(price, j) in formData.amountCurrency" :key="j">
                      {{ price.totalPrice.toFixed(2) }} <sup>{{ price.currency }}</sup>
                      <span v-if="j != formData.amountCurrency.length - 1"> | </span>
                    </span>
                  </div>
                  <div v-else class="font-medium text-gray-800">
                    {{ totalSales }} <sup v-if="idWharhouse">{{ idWharhouse.currency }}</sup>
                  </div>
                </div>

                <div class="flex justify-between mb-2">
                  <span class="text-gray-800">Total Fees:</span>
                  <div v-if="formData.amountCurrency && formData.infoCurrency && formData.infoCurrency.length > 0 && formData.amountCurrency.length > 0" class="font-medium text-gray-800">
                    <span v-for="(price, j) in formData.amountCurrency" :key="j">
                      {{ price.fees.toFixed(2) }} <sup>{{ price.currency }}</sup>
                      <span v-if="j != formData.amountCurrency.length - 1"> | </span>
                    </span>
                  </div>
                  <div v-else class="font-medium text-gray-800">
                    {{ totalFees }} <sup v-if="idWharhouse">{{ idWharhouse.currency }}</sup>
                  </div>
                </div>
              </div>

              <div v-if="formData.vat" class="flex justify-between mb-2">
                <span class="text-sm text-gray-600">TVA ({{ formData.vat }}%)</span>
                <div class="text-sm text-gray-600">{{ formData.vatFees }} <sup v-if="idWharhouse">{{ idWharhouse.currency }}</sup></div>
              </div>

              <div v-if="formData.transfert_fees" class="flex justify-between mb-4">
                <span class="text-sm text-gray-600">Transfert Fees</span>
                <div class="text-sm text-gray-600">{{ formData.transfert_fees }} <sup v-if="idWharhouse">{{ idWharhouse.currency }}</sup></div>
              </div>

              <div class="py-2 border-t border-b">
                <div class="flex justify-between">
                  <span class="font-bold text-gray-600">Payment net:</span>
                  <div v-if="formData.amountCurrency && formData.infoCurrency && formData.infoCurrency.length > 0 && formData.amountCurrency.length > 0">
                    <span v-for="(price, j) in formData.amountCurrency" :key="j">
                      {{ price.amount.toFixed(2) }} <sup>{{ price.currency }}</sup>
                      <span v-if="j != formData.amountCurrency.length - 1"> | </span>
                    </span>
                  </div>
                  <div v-else>{{ total.toFixed(2) }} <sup v-if="idWharhouse">{{ idWharhouse.currency }}</sup></div>
                </div>
                <div class="flex justify-between mt-2">
                  <span class="text-xl text-gray-600"><i class="material-icons">sync</i></span>
                  <div v-if="formData.amountCurrency && formData.infoCurrency && formData.infoCurrency.length > 0 && formData.amountCurrency.length > 0">
                    <div class="text-xl text-gray-800 font-bold">
                      {{ Math.round(formData.amountCurrency.map(item => item.amountMad).reduce((prev, next) => prev + next)) }} <sup>MAD</sup>
                    </div>
                  </div>
                  <div v-else class="text-xl text-gray-800 font-bold">
                    {{ (total / formData.info.currency).toFixed(2) }} <sup>MAD</sup>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <vue-modal-2 
      name="nameInvoice" 
      :headerOptions="{title: 'Payement Info'}" 
      :noFooter="true" 
      modalSize="lg" 
      @on-close="close"
      class="no-display"
    >
      <div class="bg-white rounded-lg">
        <div class="rounded-lg flex">
          <div class="w-1/4 flex justify-center items-center">
            <span class="w-16 h-16 bg-blue-600 text-white p-3 rounded-full">
              <i class="material-icons" style="font-size: 40px;">file_download</i>
            </span>
          </div>
          <div class="w-full pr-4 mt-4">
            <div class="grid grid-cols-1">
              <div class="border hover:border-blue-400 focus-within:border-blue-500 focus-within:text-blue-500 transition-all duration-500 relative rounded p-1">
                <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                  <p>
                    <label for="fullname" class="bg-white text-gray-700 px-1">Invoice Name <span class="text-red-500">*</span></label>
                  </p>
                </div> 
                <p>
                  <input type="text" v-model="InvoiceName" class="py-2 text-xs px-2 outline-none block h-full w-full"> 
                </p>
              </div> 
            </div>
          </div>
        </div>
        <div class="p-4 flex space-x-4">
          <a href="#" @click="close" class="w-1/2 px-4 py-3 text-center bg-gray-500 text-white hover:bg-gray-400 font-bold rounded-lg text-sm">Cancel</a>
          <a href="#" @click="generateReport" class="w-1/2 px-4 py-3 text-center text-blue-100 bg-blue-600 rounded-lg hover:bg-blue-700 hover:text-white font-bold text-sm">Confirme</a>
        </div>
      </div>
    </vue-modal-2>
  </div>
</template>

<script>
import jsPDFInvoiceTemplate, { OutputType, jsPDF } from "jspdf-invoice-template";
import image from "@/assets/colismart.png";
//import jsPDF from 'jspdf'

//or directly in browser
export default {
  props: {
     currentUser: { type: Object},
     idWharhouse: { type: Object},
     idPayment: { type: String},
   },
  data() {
    return {
      formData: {
        details: [],
      },
      expeditionCount:0,
      InvoiceName:"",
      image:image,
      fullPage: false,
      loading:false,
      content: [],
      headers: [{title:this.$t('order_id')},{title:this.$t('country')},{title:this.$t('products')},{title:this.$t('total_price')},{title:this.$t('confirmation')},{title:this.$t('shipping')},{title:this.$t('fulfillement')},{title:this.$t('total')}],
      total:0,
      totalFees:0,
      totalSales:0,
      options: this.$countries,
      currency:"",
     // currentUser: {},
      invoiceData:{},
      dataGroupedByCountry:[],
      dataGroupedExpeditionsByCountry:[],
      dataGroupedRefundsByCountry:[],
      dataGroupedChargesByCountry:[],
      dataProducts:[],
      optionsCountries: this.$countries,
      idP:this.idPayment,
    };
  },
  watch: {
    'idPayment': async function () {
       console.log('idPayment------',this.idPayment)
       this.idP=this.idPayment;
       await this.dataToEdit();
    },
  },
  async mounted() {
    console.log('idWharhouse------------- 22',this.idWharhouse)
    console.log('idPayment------------- 22',this.idP)
    //await this.getCurrency();
   // await this.getUser();
   await this.dataToEdit();
    
  },
  methods: {
    ProductGroupNmae(data,currency){
      let product="";
      let typeExp='Prix de 1 kilo: ';
      if(data.type && data.type === 'transfer') typeExp='Prix de 1 colis: ';
      for (let i in data.details) {
        if(data.details[i].name) product+=data.details[i].name+` (<span style="text-decoration: underline overline limegreen double 1.2px;">${typeExp+data.details[i].price_one_kiloOrPackage} <sup>${currency}</sup>)</span>`;
        if(data.details.length-1 != i) product+=" | "; 
      }
      return product;
    },
      getElementInArray(data,value){
        var result  = data.filter(function(o){
            let whar=o.warehouse;
            if(o.warehouse._id) whar=o.warehouse._id;
            return whar == value.toString();
        } );
        return result? result[0] : null; // or undefined
    },
    sumObjAmountCurrency(objArr,infoCurrency=[],refunds=[],expeditions=[],charges=[]) {
        var newArr = [];
        infoCurrency.forEach(row2 => { 
            var index = objArr.findIndex(x => x.currency == row2.currencyName); 
            if(index === -1) objArr.push({
              "count": 0,
              "currency": row2.currencyName,
              "country": row2.country,
              "totalPrice": 0,
              "amount": 0,
              "fees": 0
            })
        });

        expeditions.forEach(row => { row.type='expedition'; });
        charges.forEach(row => { row.type='charge'; });

        if(refunds.length>0) objArr = objArr.concat(refunds);
        if(expeditions.length>0) objArr = objArr.concat(expeditions);
        if(charges.length>0) objArr = objArr.concat(charges);

        //amount by mad
        objArr.forEach(row => { 
              row.amountSales_Fees=0;
              row.amountSales_FeesMad=0;
              row.amountMad=0;
              row.fees_amountMad=0;

              row.fees_refoundMad=0;
              row.fees_expeditionMad=0;
              row.fees_chargeMad=0;

              if(!row.amount) row.amount=0;
              if(!row.fees) row.fees=0;
              if(!row.totalPrice) row.totalPrice=0;
              if(!row.fees_amount) row.fees_amount=0;

              const getCurrency = infoCurrency.find(o => o.currencyName === row.currency);
                if(getCurrency && row.type && ['expedition','charge'].indexOf(row.type) !== -1){
                     row.amountMad = row.amount/getCurrency.currencyCredit;
                     row.fees_amountMad = row.fees_amount/getCurrency.currencyCredit;
                     if(row.type === "expedition") row.fees_expeditionMad = row.fees_amount/getCurrency.currencyCredit;
                     if(row.type === "charge") row.fees_chargeMad = row.fees_amount/getCurrency.currencyCredit;
                }else {
                   row.amountMad = row.amount/getCurrency.currency;
                   row.fees_amountMad = row.fees_amount/getCurrency.currency;
                   row.amountSales_FeesMad = (row.totalPrice-row.fees)/getCurrency.currency;
                   row.fees_refoundMad = row.fees_amount/getCurrency.currency;
                }
               
        });

        console.log('newArr------------11',objArr)

      let result = objArr.reduce((acc, curr) => {
        console.log('acc------------11',acc)
            let item = acc.find(item => item.currency === curr.currency);

            if (item) {
             
              if(!item.amountSales_Fees) item.amountSales_Fees=0; if(!curr.amountSales_Fees) curr.amountSales_Fees=0;
              if(!item.amount) item.amount=0; if(!curr.amount) curr.amount=0;
              if(!item.count) item.count=0; if(!curr.count) curr.count=0;
              if(!item.fees) item.fees=0; if(!curr.fees) curr.fees=0;
              if(!item.totalPrice) item.totalPrice=0; if(!curr.totalPrice) curr.totalPrice=0;
              if(!item.fees_amount) item.fees_amount=0; if(!curr.fees_amount) curr.fees_amount=0;

              if(!item.amountMad) item.amountMad=0; if(!curr.amountMad) curr.amountMad=0;
              if(!item.fees_amountMad) item.fees_amountMad=0; if(!curr.fees_amountMad) curr.fees_amountMad=0;
              if(!item.amountSales_FeesMad) item.amountSales_FeesMad=0; if(!curr.amountSales_FeesMad) curr.amountSales_FeesMad=0;

              if(!item.fees_refoundMad) item.fees_refoundMad=0; if(!curr.fees_refoundMad) curr.fees_refoundMad=0;
              if(!item.fees_expeditionMad) item.fees_expeditionMad=0; if(!curr.fees_expeditionMad) curr.fees_expeditionMad=0;
              if(!item.fees_chargeMad) item.fees_chargeMad=0; if(!curr.fees_chargeMad) curr.fees_chargeMad=0;

                item.amount += curr.amount;
                item.amountMad += curr.amountMad;
                item.count += curr.count;
                
                item.fees += curr.fees;
                item.totalPrice += curr.totalPrice;
                item.amountSales_Fees = item.totalPrice - item.fees;
                item.amountSales_Fees += curr.amountSales_Fees;
                item.amount -= curr.fees_amount;
                item.amountMad -= curr.fees_amountMad;

               
                item.fees_refoundMad += curr.fees_refoundMad;
                item.fees_expeditionMad += curr.fees_expeditionMad;
                item.fees_chargeMad += curr.fees_chargeMad;
                item.amountSales_FeesMad += curr.amountSales_FeesMad;
                
            } else {
              if(!curr.amountSales_Fees) curr.amountSales_Fees=0;
              if(!curr.fees_expeditionMad) curr.fees_expeditionMad=0;
              if(!curr.fees_refoundMad) curr.fees_refoundMad=0;
              if(!curr.fees_chargeMad) curr.fees_chargeMad=0;
              if(!curr.amount) curr.amount=0;
              if(!curr.count) curr.count=0;
              if(!curr.fees) curr.fees=0;
              if(!curr.totalPrice) curr.totalPrice=0;
              if(!curr.fees_amount) curr.fees_amount=0;
              if(!curr.amountMad) curr.amountMad=0;
              acc.push(curr);
            }

            return acc;
      }, []);
      console.log('newArr------------ 22',result)
      return result;

      

    },
    RouterBack: function() {
       this.$router.back();
    },
   async sumProduct(data){
      console.log('data---------',data)
      let product="";
      for (let i in data) {
         const editData = await this.$server.get("products", {id:data[i].product,});
        product+=editData.content.name
        console.log('product---------',product)
        if(data.length-1 != i) product+=" ,"; 
      }
      return product;
    },
    async getCurrency(){
      await this.$store.dispatch('wharhouse/getCurrency').then((res) => {
              this.currency=res;
          })
         // this.currency='XOF';
    },
    close(){
         this.$vm2.close('nameInvoice');
    },
    openPopup(){
       this.$vm2.open('nameInvoice');
    },
    async generateReport() {
      if(!this.InvoiceName){
        alert("File name required", "warning");
        return false;
      }

      var mywindow = window.open('', 'PRINT', 'height=400,width=600');
      mywindow.document.write(`<html lang="en"><head><meta charset="utf-8"><title>${this.InvoiceName}</title>`);
      mywindow.document.write(`<style type="text/css">
        /* Reset and basic styles */
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: Arial, sans-serif; 
          font-size: 12px; 
          line-height: 1.4; 
          color: #333;
          margin: 0;
          padding: 15px;
        }
        
        /* Layout */
        .container { max-width: 100%; margin: 0 auto; }
        .no-display { display: none !important; }
        .flex { display: flex; }
        .justify-between { justify-content: space-between; }
        .items-center { align-items: center; }
        .w-full { width: 100%; }
        .mb-4 { margin-bottom: 16px; }
        .mb-6 { margin-bottom: 24px; }
        .mb-8 { margin-bottom: 32px; }
        .p-4 { padding: 16px; }
        .px-4 { padding-left: 16px; padding-right: 16px; }
        .py-2 { padding-top: 8px; padding-bottom: 8px; }
        .border { border: 1px solid #ddd; }
        .border-b { border-bottom: 1px solid #ddd; }
        .rounded { border-radius: 4px; }
        .bg-gray-50 { background-color: #f9f9f9; }
        .bg-gray-100 { background-color: #f5f5f5; }
        .logo {width: 150px; height: auto;}
        
        /* Invoice Header */
        .invoice-header { 
          display: flex; 
          justify-content: space-between; 
          align-items: flex-start; 
          margin-bottom: 30px; 
          padding-bottom: 20px; 
          border-bottom: 2px solid #ddd;
        }
        .invoice-title { 
          font-size: 28px; 
          font-weight: bold; 
          color: #333; 
          margin-bottom: 5px;
        }
        .invoice-number { color: #e53e3e; }
        .logo-container { text-align: right; }
        .logo-container img { 
          max-width: 120px; 
          height: auto; 
          opacity: 0.8;
        }
        
        /* Grid for info cards */
        .info-grid { 
          display: grid; 
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); 
          gap: 15px; 
          margin-bottom: 30px; 
        }
        .info-card { 
          background: #f8f8f8; 
          border: 1px solid #ddd; 
          border-radius: 4px; 
          padding: 12px; 
        }
        .info-card h3 { 
          font-size: 11px; 
          color: #666; 
          text-transform: uppercase; 
          font-weight: 600; 
          margin-bottom: 5px; 
        }
        .info-card p { 
          font-size: 12px; 
          color: #333; 
          font-weight: 500; 
        }
        
        /* Section headers */
        .section-title { 
          font-size: 16px; 
          font-weight: bold; 
          color: #333; 
          margin-bottom: 15px; 
          padding-bottom: 8px; 
          border-bottom: 1px solid #ddd; 
        }
        
        /* Tables */
        .table-container { 
          margin-bottom: 25px; 
          border: 1px solid #ddd; 
          border-radius: 4px; 
          overflow: hidden; 
        }
        .table-header { 
          background: #4a5568; 
          color: white; 
          padding: 10px 15px; 
          font-weight: bold; 
          font-size: 13px; 
        }
        table { 
          width: 100%; 
          border-collapse: collapse; 
          font-size: 11px; 
        }
        table th { 
          background: #e2e8f0; 
          color: #2f855a; 
          font-weight: bold; 
          padding: 8px 6px; 
          text-align: left; 
          font-size: 10px; 
          text-transform: uppercase; 
          border-bottom: 1px solid #ccc; 
        }
        table td { 
          padding: 6px; 
          border-bottom: 1px solid #eee; 
          vertical-align: top; 
        }
        table tbody tr:nth-child(even) { background-color: #f9f9f9; }
        table tbody tr:nth-child(odd) { background-color: white; }
        table tfoot td { 
          background-color: #e2e8f0; 
          font-weight: bold; 
          border-top: 2px solid #ccc; 
        }
        
        /* Special row highlighting */
        .affiliate-row { background-color: #e6fffa !important; border-left: 3px solid #319795; }
        
        /* Credits section */
        .credits-section { margin-top: 30px; }
        .refunds-table .table-header { background: #fed7d7; color: #9b2c2c; }
        .expeditions-table .table-header { background: #bee3f8; color: #2c5282; }
        .charges-table .table-header { background: #fefcbf; color: #975a16; }
        
        /* Summary section */
        .summary-section { 
          margin-top: 30px; 
          float: right; 
          width: 350px; 
          clear: both; 
        }
        .summary-card { 
          background: white; 
          border: 1px solid #ddd; 
          border-radius: 4px; 
          padding: 20px; 
        }
        .summary-row { 
          display: flex; 
          justify-content: space-between; 
          padding: 5px 0; 
          border-bottom: 1px solid #eee; 
        }
        .summary-row:last-child { border-bottom: none; }
        .summary-row.total { 
          background: #f0fff4; 
          border: 2px solid #38a169; 
          border-radius: 4px; 
          padding: 10px; 
          font-weight: bold; 
          font-size: 14px; 
        }
        
        /* Text utilities */
        .text-xs { font-size: 10px; }
        .text-sm { font-size: 11px; }
        .text-base { font-size: 12px; }
        .text-lg { font-size: 14px; }
        .text-xl { font-size: 16px; }
        .font-medium { font-weight: 500; }
        .font-semibold { font-weight: 600; }
        .font-bold { font-weight: bold; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .text-gray-600 { color: #666; }
        .text-gray-700 { color: #555; }
        .text-gray-800 { color: #333; }
        .text-red-600 { color: #e53e3e; }
        .text-blue-600 { color: #3182ce; }
        .text-green-600 { color: #38a169; }
        .text-purple-600 { color: #805ad5; }
        .text-teal-600 { color: #319795; }
        .uppercase { text-transform: uppercase; }
        
        /* Clear floats */
        .clearfix::after { 
          content: ""; 
          display: table; 
          clear: both; 
        }
        
        /* Print optimizations */
        
        /* Text utilities */
        .text-xs { font-size: 0.75rem; }
        .text-sm { font-size: 0.875rem; }
        .text-lg { font-size: 1.125rem; }
        .text-xl { font-size: 1.25rem; }
        .text-2xl { font-size: 1.5rem; }
        .text-3xl { font-size: 1.875rem; }
        .font-medium { font-weight: 500; }
        .font-semibold { font-weight: 600; }
        .font-bold { font-weight: 700; }
        .uppercase { text-transform: uppercase; }
        
        /* Colors */
        .text-gray-600 { color: #718096; }
        .text-gray-700 { color: #4a5568; }
        .text-gray-800 { color: #2d3748; }
        .text-gray-900 { color: #1a202c; }
        .text-red-500 { color: #f56565; }
        .text-red-600 { color: #e53e3e; }
        .text-green-700 { color: #2f855a; }
        .text-blue-600 { color: #3182ce; }
        .text-blue-900 { color: #1e3a8a; }
        .text-purple-600 { color: #805ad5; }
        .text-teal-600 { color: #319795; }
        .text-yellow-800 { color: #975a16; }
        .text-red-700 { color: #c53030; }
        .text-red-800 { color: #9b2c2c; }
        .text-blue-700 { color: #2b6cb0; }
        .text-blue-800 { color: #2c5282; }
        .text-green-600 { color: #38a169; }
        
        /* Background colors */
        .bg-white { background-color: #ffffff; }
        .bg-gray-50 { background-color: #f9fafb; }
        .bg-gray-100 { background-color: #f7fafc; }
        .bg-gray-200 { background-color: #edf2f7; }
        .bg-gray-700 { background-color: #4a5568; }
        .bg-red-100 { background-color: #fed7d7; }
        .bg-red-200 { background-color: #fbb6ce; }
        .bg-blue-50 { background-color: #ebf8ff; }
        .bg-blue-200 { background-color: #bee3f8; }
        .bg-green-50 { background-color: #f0fff4; }
        .bg-green-200 { background-color: #c6f6d5; }
        .bg-yellow-50 { background-color: #fffff0; }
        .bg-yellow-200 { background-color: #fefcbf; }
        .bg-teal-100 { background-color: #e6fffa; }
        
        /* Layout utilities */
        .flex { display: flex; }
        .flex-1 { flex: 1 1 0%; }
        .grid { display: grid; }
        .block { display: block; }
        .inline-block { display: inline-block; }
        .w-full { width: 100%; }
        .w-40 { width: 10rem; }
        .w-1/4 { width: 25%; }
        .w-2/4 { width: 50%; }
        .w-3/4 { width: 75%; }
        .h-auto { height: auto; }
        .min-w-full { min-width: 100%; }
        
        /* Spacing */
        .m-0 { margin: 0; }
        .mt-0 { margin-top: 0; }
        .mt-1 { margin-top: 0.25rem; }
        .mt-4 { margin-top: 1rem; }
        .mt-5 { margin-top: 1.25rem; }
        .mt-10 { margin-top: 2.5rem; }
        .mb-0 { margin-bottom: 0; }
        .mb-2 { margin-bottom: 0.5rem; }
        .mb-3 { margin-bottom: 0.75rem; }
        .mb-4 { margin-bottom: 1rem; }
        .mb-5 { margin-bottom: 1.25rem; }
        .mb-6 { margin-bottom: 1.5rem; }
        .mb-8 { margin-bottom: 2rem; }
        .ml-auto { margin-left: auto; }
        .ml-1 { margin-left: 0.25rem; }
        .p-0 { padding: 0; }
        .p-1 { padding: 0.25rem; }
        .p-2 { padding: 0.5rem; }
        .p-4 { padding: 1rem; }
        .p-6 { padding: 1.5rem; }
        .px-1 { padding-left: 0.25rem; padding-right: 0.25rem; }
        .px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
        .px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
        .px-4 { padding-left: 1rem; padding-right: 1rem; }
        .px-5 { padding-left: 1.25rem; padding-right: 1.25rem; }
        .px-6 { padding-left: 1.5rem; padding-right: 1.5rem; }
        .py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
        .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
        .py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
        .py-4 { padding-top: 1rem; padding-bottom: 1rem; }
        .pt-0 { padding-top: 0; }
        .pt-4 { padding-top: 1rem; }
        .pl-3 { padding-left: 0.75rem; }
        .pl-4 { padding-left: 1rem; }
        .pr-2 { padding-right: 0.5rem; }
        
        /* Alignment */
        .text-left { text-align: left; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .items-center { align-items: center; }
        .justify-between { justify-content: space-between; }
        .align-top { vertical-align: top; }
        
        /* Borders */
        .border { border-width: 1px; border-color: #e2e8f0; }
        .border-b { border-bottom-width: 1px; border-color: #e2e8f0; }
        .border-t { border-top-width: 1px; border-color: #e2e8f0; }
        .border-b-2 { border-bottom-width: 2px; border-color: #e2e8f0; }
        .border-t-2 { border-top-width: 2px; border-color: #e2e8f0; }
        .border-l-2 { border-left-width: 2px; border-color: #3182ce; }
        .border-gray-200 { border-color: #e2e8f0; }
        .border-gray-300 { border-color: #cbd5e0; }
        .border-blue-200 { border-color: #bee3f8; }
        .border-green-200 { border-color: #c6f6d5; }
        .border-red-200 { border-color: #fed7d7; }
        .border-yellow-200 { border-color: #fefcbf; }
        .rounded { border-radius: 0.25rem; }
        .rounded-lg { border-radius: 0.5rem; }
        .rounded-full { border-radius: 9999px; }
        
        /* Overflow */
        .overflow-x-auto { overflow-x: auto; }
        .whitespace-no-wrap { white-space: nowrap; }
        .overflow-visible { white-space: initial; }
        
        /* Special elements */
        .shadow { box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06); }
        .shadow-sm { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
        sup { top: -0.5em; font-size: 75%; line-height: 0; position: relative; vertical-align: baseline; }
        .v-super { vertical-align: super; }
        .leading-4 { line-height: 1rem; }
        .tracking-wider { letter-spacing: 0.05em; }
        
        /* Custom classes for specific elements */
        .empty-row { background-color: #f9f9f9; }
        .trakingn img { width: 50%; }
        .imgg { vertical-align: inherit; width: 40px; display: inline-block; }
        
        /* Grid utilities */
        .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
        .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        .gap-4 { gap: 1rem; }
        .gap-6 { gap: 1.5rem; }
        
        /* Print optimizations */
        @media print {
          @page { 
            size: A4; 
            margin: 10mm; 
          }
          body { 
            margin: 0; 
            padding: 0; 
            font-size: 11px !important; 
          }
          .no-display { display: none !important; }
          table { 
            page-break-inside: avoid; 
            font-size: 10px !important; 
          }
          .table-container { 
            page-break-inside: avoid; 
            margin-bottom: 15px; 
          }
          .summary-section { 
            page-break-inside: avoid; 
          }
        }
        
        /* Responsive adjustments */
        @media screen and (max-width: 768px) {
          .info-grid { grid-template-columns: 1fr; }
          .summary-section { width: 100%; float: none; }
          table { font-size: 10px; }
          table th, table td { padding: 4px; }
        }
      </style>`);
      mywindow.document.write('</head><body>');
      mywindow.document.write(document.getElementById("detailP").innerHTML);
      mywindow.document.write('</body></html>');
      mywindow.document.close();
      mywindow.focus();
      
      const self = this;
      setTimeout(function () {
        mywindow.print();
        mywindow.close();
        self.$vm2.close('nameInvoice');
      }, 500);
      return false;
    },    
    async getUser() {
      const res = await this.$me;
      this.currentUser = res.content;
    },
    async getTotal(data) {
     this.total=0;
     this.totalFees=0;
     this.totalSales=0;
     console.log('data------------',data)
     if(data && data.length){
        data.forEach(item => {
               this.total += item.amount;
               this.totalFees+=(item.confirmation_vat+item.fulfillement_fees_vat+item.shipping_vat);
               this.totalSales += item.order.totalPrice;
       });
     }
    },
    ProductNameSplit(data){
      let product="";
      console.log('ProductNameSplit-------------')
      for (let i in data) {
        if(data[i].name) product+=data[i].name
        if(data.length-1 != i) product+=" |"; 
      }
      return product;
    },
    async dataToEdit() {
      if (this.$route.params.id || this.idP) {
          this.loading=true;
         if(this.idP) this.$store.dispatch("actionloadingemail",{loadingEmail:'envoi'});
          this.dataProducts=[];
          console.log('this.$route.params.id-------------',this.$route.params.id||this.idP)
          const data = await this.$server.get("payments", {id: this.$route.params.id||this.idP,}); 
          
          if(data && data.content){
            let finalExcuse=false;
              this.formData = data.content;
              console.log(' this.formData 111', this.formData.details)
              this.formData.details=this.formData.details.sort((a,b) => (a.country > b.country) ? 1 : ((b.country > a.country) ? -1 : 0))
              this.expeditionCount=this.formData.expeditions.length;
              this.dataGroupedByCountry= _.chain(this.formData.details).groupBy("country").map((value, key) => ({ 
                country: key,currency:value[0].currency||null,
                count: value.length,
                orders: value,
                fees:value.map(item => item.confirmation_vat+item.fulfillement_fees_vat+item.shipping_cancel_vat+item.shipping_refund_vat+item.shipping_vat+(item.quality_control||0)+(item.upsell_vat||0)).reduce((prev, next) => prev + next),
                sales: value.map(item => item.affiliate && item.affiliate.type?item.affiliate.commission:item.POD && item.POD.name?item.POD.commission:item.order.totalPrice).reduce((prev, next) => prev + next), 
                amount:value.map(item => item.amount).reduce((prev, next) => prev + next) 
              })).value();
              this.dataGroupedExpeditionsByCountry= _.chain(this.formData.expeditions).groupBy("country").map((value, key) => ({ country: key,currency:value[0].currency||null,count: value.length, expeditions: value,fees:value.map(item => item.fees_amount).reduce((prev, next) => prev + next) })).value();
              this.dataGroupedRefundsByCountry= _.chain(this.formData.refunds).groupBy("country").map((value, key) => ({ country: key,currency:value[0].currency||null,count: value.length, refunds: value,fees:value.map(item => item.fees_amount).reduce((prev, next) => prev + next) })).value();
              this.dataGroupedChargesByCountry= _.chain(this.formData.charges).groupBy("country").map((value, key) => ({ country: key,currency:value[0].currency||null,count: value.length, charges: value,fees:value.map(item => item.fees_amount).reduce((prev, next) => prev + next) })).value();
              console.log('dataGroupedByCountry-----------',this.dataGroupedByCountry)   
              console.log('expeditions r-----------',this.formData.expeditions)   
              this.getTotal(this.formData.details);
                if(this.formData.details[0] && this.formData.details[0]){
                    // if(this.formData.details[0].confirmation_vat===0)  this.headers.splice(this.headers.findIndex(item => item === this.$t('confirmation')), 1)
                    // if(this.formData.details[0].fulfillement_fees_vat===0)  this.headers.splice(this.headers.findIndex(item => item === this.$t('fulfillement')), 1)
                    // if(this.formData.details[0].quality_control===0) this.headers.splice(this.headers.findIndex(item => item === this.$t('quality_control')), 1)
                    // if(this.formData.country!='MA') this.headers.splice(this.headers.findIndex(item => item === this.$t('city')), 1)
                }
                let iP = 0;
              for (const payment of this.formData.details) {
                  let product="";

                  if(payment.order.details && payment.order.details[0] && !payment.order.details[0].name){
                       console.log('----pr not exiist----')
                         for (let i in payment.order.details) {

                            if(payment.order.details[i] && payment.order.details[i].product){
                                if(!this.dataProducts.find(o => o.id.toString() === payment.order.details[i].product.toString())){
                                  const editData = await this.$server.get("products", {id:payment.order.details[i].product,});
                                  this.dataProducts.push({id:payment.order.details[i].product.toString(),name:editData.content.name})
                                }
                                product+=this.dataProducts.find(o => o.id.toString() === payment.order.details[i].product.toString()).name
                                if(payment.order.details.length-1 != i) product+=" | "; 
                                payment.products=product;
                            }    

                         }
                   }
                   
                  iP++;     
                 if(iP === this.formData.details.length) finalExcuse=true;
              }
              if(this.formData.infoCurrency){
                  for (let i in this.formData.infoCurrency) {
                      if(this.formData.infoCurrency[i].currency == 1){
                          const resW = await this.$server.get("warehouses", { country: this.formData.infoCurrency[i].country}); 
                          console.log('resW.content._id--------',resW.content._id)
                          const dataWahrhouse = await this.$server.get("settings",{warehouse:resW.content._id});
                          console.log('dataWahrhouse--------',dataWahrhouse)
                          const currentSettingW= this.getElementInArray(dataWahrhouse.content[0].details,resW.content._id);
                          this.formData.infoCurrency[i].currency = currentSettingW.info.currency;
                      }     
                  }
              }else{
                  if(!this.formData.info || (this.formData.info && this.formData.info.currency ==1)){
                      const resW = await this.$server.get("warehouses", { country: this.formData.country}); 
                      console.log('resW.content._id--------',resW.content._id)
                      const dataWahrhouse = await this.$server.get("settings",{warehouse:resW.content._id});
                      console.log('dataWahrhouse--------',dataWahrhouse)
                      const currentSettingW= this.getElementInArray(dataWahrhouse.content[0].details,resW.content._id);
                      this.formData.info.currency=currentSettingW.info.currency;
                  }
              }
            
              if(this.formData.amountCurrency &&  this.formData.infoCurrency && this.formData.infoCurrency.length>0 && this.formData.amountCurrency.length>0){
                    this.formData.amountCurrency =this.sumObjAmountCurrency(this.formData.amountCurrency,this.formData.infoCurrency,this.formData.amountRefundsCurrency||[],this.formData.amountExpeditionsCurrency||[],this.formData.amountChargesCurrency||[]);
                    this.invoiceData.warehouses="";        
                    this.formData.country.forEach(function (war,j) {
                      this.invoiceData.warehouses += this.$f.getCountryNameByCode(this.options,war)+`${j!=this.formData.country.length-1?', ':''}`;        
                    }.bind(this));
                    this.invoiceData.sales=this.invoiceData.fees=this.invoiceData.amount="";   
                
                  this.formData.amountCurrency.forEach(function (price,j) {
                      this.invoiceData.sales += `${price.totalPrice.toFixed(2)} ${price.currency}${j!=this.formData.amountCurrency.length-1?'|':''}`;  
                      this.invoiceData.fees += `${price.fees.toFixed(2)} ${price.currency}${j!=this.formData.amountCurrency.length-1?'|':''}`;  
                      this.invoiceData.amount += `${price.amount.toFixed(2)} ${price.currency}${j!=this.formData.amountCurrency.length-1?'|':''}`;        
                  }.bind(this));

                  this.invoiceData.mad=this.formData.amountCurrency.map(item => {
                      const objC = this.formData.infoCurrency.find(o => o.currencyName === item.currency); 
                      return item.amount/objC.currency;
                  }).reduce((prev, curr) => prev + curr, 0).toFixed(2);          
              }
              this.loading=false;  
              if(this.idP){
                setTimeout(() => (this.$store.dispatch("actionloadingemail",{loadingEmail:'final'})), 1300);
              } 
           
          }

         
        }
    }
  },
};
</script>

<style scoped>
.login-or {
  position: relative;
  color: rgb(0 0 0 / 55%);;
  margin-top: 10px;
  margin-bottom: 10px;
  padding-top: 10px;
  padding-bottom: 10px;
}
.hr-or {
  height: 2px;
    margin-top: 0px !important;
    margin-bottom: 0px !important;
    border: 0;
    border-top: 2px solid rgb(0 0 0 / 55%);
}
.span-or {
  display: block;
    position: absolute;
    left: 39%;
    top: -2px;
    background-color: #fff;
    width: 190px;
    text-align: center;
    font-weight: 700;
}
.invoice {
  display: flex;
}
.invoice-title {
    color: #999;
    font-size: 1.875rem;
    line-height: 2.25rem;
    margin-bottom: 0;
    font-weight: 700;
    text-transform: uppercase;
}
.invoice-des {
  text-align: right;
  margin-left: auto;
}
.invoice-logo{
    margin-left: auto;
    width: 10.5rem;
    height: auto;
    display: block;
    vertical-align: middle;
}
caption {
  background: #cdcdcd;
  font-weight: bold;
  font-size: 1.1em;
  margin-left: -1px; /* Fix FF 1px issue */
}
.w-40{
  width: 25em;
}
.empty-row{
   background: repeating-linear-gradient( 53deg , #bfc0c2 4%, #fcfcff 11px 6%, #ffffff 10px 7%, #9d9c9c 15px);
}
/* animation for vue transition tag */
dialog[open] {
  animation: appear 0.15s cubic-bezier(0, 1.8, 1, 1.8);
}
dialog::backdrop {
  background: linear-gradient(45deg, rgba(0, 0, 0, 0.5), rgba(54, 54, 54, 0.5));
  backdrop-filter: blur(3px);
}
table{max-width: 100%;overflow: scroll;}
@keyframes appear {
  from {
    opacity: 0;
    transform: translateX(-3rem);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>

